import { css, keyframes } from '@emotion/react';

const STROKE_LIGHT = '#eeeeee';
const ARROW_SIZE = 8;

const pulseOnce = keyframes({
  '0%': { opacity: 0.4 },
  '100%': { opacity: 0 },
});

const arrowPositionMap = {
  center: { left: '50%', right: 'auto', marginLeft: -ARROW_SIZE },
  left: { left: 20, right: 'auto', marginLeft: 0 },
  right: { left: 'auto', right: 20, marginLeft: 0 },
};

const makeSmartMatchMeterV2Styling = (confidence) => ({
  root: css({
    position: 'relative',
    width: '100%',
    maxWidth: 480,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
    margin: '0 auto',
    fontFamily: "'Arial', sans-serif",
    opacity: 0,
    transition: '500ms ease opacity',
  }),
  card: css({
    position: 'relative',
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'all 500ms ease',
    background:
      confidence === 'high'
        ? 'linear-gradient(155deg, #effdff 7%, #f7f3ff 89%)'
        : '#ffffff',
    border:
      confidence === 'high' ? '1px solid #e8e0f0' : `1px solid ${STROKE_LIGHT}`,
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 8,
  }),
  leftSection: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flex: 1,
  }),
  iconBox: css({
    width: 30,
    height: 30,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#673ab7',
  }),
  title: css({
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#171717',
  }),
  rightSection: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  }),
  tooltipPosition: css({
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: ARROW_SIZE + 4,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 200ms ease, visibility 200ms ease',
    zIndex: 10,
    pointerEvents: 'none',
  }),
  // SparkleIcon
  sparkleIcon: (size = 18, color = '#ffffff') =>
    css({
      width: size,
      height: size,
      color,
      display: 'block',
    }),
  // MeterBadge
  badgeContainer: (width) =>
    css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      fontFamily: "'Arial', sans-serif",
      ...(width && { width, flexShrink: 0 }),
    }),
  badgeLabel: css({
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: '#525252',
  }),
  badgeWrapper: css({
    position: 'relative',
  }),
  badge: (bg, borderColor, width) =>
    css({
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2px 8px',
      gap: 4,
      width: width || 'fit-content',
      maxWidth: width || 'none',
      height: 20,
      boxSizing: 'border-box',
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      backgroundColor: bg,
      transition: 'all 500ms ease',
      overflow: 'hidden',
    }),
  badgeValue: (textColor) =>
    css({
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '24px',
      whiteSpace: 'nowrap',
      color: textColor,
      transition: 'all 500ms ease',
    }),
  badgeDot: css({
    width: 8,
    height: 8,
    borderRadius: '50%',
    transition: 'all 500ms ease',
  }),
  // PulseOverlay
  pulseOverlay: (color) =>
    css({
      position: 'absolute',
      top: -4,
      right: -4,
      bottom: -4,
      left: -4,
      borderRadius: 12,
      backgroundColor: color,
      animation: `${pulseOnce} 500ms ease-out forwards`,
    }),
  // MeterProgressBar
  progressTrack: css({
    width: '100%',
    height: 4,
    backgroundColor: '#eeeeee',
    overflow: 'hidden',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  }),
  progressBar: (progress, barColor) =>
    css({
      height: '100%',
      width: `${Math.min(95, Math.max(0, progress))}%`,
      backgroundColor: barColor,
      transition: 'all 500ms ease-out',
    }),
  // InfoButton
  infoButton: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    transition: 'color 200ms ease',
    '&:hover': {
      color: '#374151',
    },
  }),
  infoIcon: css({
    width: 16,
    height: 16,
  }),
  // Tooltip
  tooltip: () =>
    css({
      position: 'relative',
      padding: '12px 16px',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      border: `1px solid ${STROKE_LIGHT}`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      fontSize: 13,
      lineHeight: 1.5,
      color: '#525252',
      fontFamily: "'Arial', sans-serif",
      fontWeight: 400,
    }),
  tooltipArrow: (arrowPosition) =>
    css({
      position: 'absolute',
      top: -ARROW_SIZE,
      left: arrowPositionMap[arrowPosition].left,
      right: arrowPositionMap[arrowPosition].right,
      marginLeft: arrowPositionMap[arrowPosition].marginLeft,
      width: 0,
      height: 0,
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid ${STROKE_LIGHT}`,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 1,
        left: -(ARROW_SIZE - 1),
        width: 0,
        height: 0,
        borderLeft: `${ARROW_SIZE - 1}px solid transparent`,
        borderRight: `${ARROW_SIZE - 1}px solid transparent`,
        borderBottom: `${ARROW_SIZE - 1}px solid #ffffff`,
      },
    }),
});

export default makeSmartMatchMeterV2Styling;
